import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../../../services/AxiosInstance";
import { notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import DatePickerRanger from "jsx/components/CitoPlus/components/DatePickerRanger";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import {
  ZonaSocialSelect,
  useZonaSocialOptions,
} from "jsx/components/CitoPlus/components/ZonaSocial";

const REPORTES_URL = "/api/v3/zonas-sociales/reportes/";

const formatNumber = (value) =>
  Number.isFinite(value)
    ? value.toLocaleString("es-CO", { minimumFractionDigits: 0 })
    : "0";

const formatHours = (value) =>
  Number.isFinite(value)
    ? value.toLocaleString("es-CO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "0.00";

const defaultFilters = Object.freeze({
  zona: "",
  inmueble: "",
  range: { start: null, end: null },
});

const ReportesZonasSociales = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    periodo: { desde: null, hasta: null },
    resumen_global: {
      total_reservas: 0,
      total_horas_reservadas: 0,
      ingresos_generados: 0,
    },
    por_zona: [],
    por_inmueble: [],
  });

  const {
    zones,
    isLoading: isLoadingZones,
    reload: reloadZones,
  } = useZonaSocialOptions({
    onError: () => notifyError("No fue posible obtener las zonas sociales."),
  });

  const handleRangeChange = useCallback(([start, end]) => {
    setFilters((prev) => ({ ...prev, range: { start, end } }));
  }, []);

  const handleZonaChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, zona: value || "" }));
  }, []);

  const handleInmuebleChange = useCallback((event) => {
    setFilters((prev) => ({ ...prev, inmueble: event.target.value }));
  }, []);

  const buildParams = useCallback(
    (state) => {
      const current = state ?? filters;
      const params = {};
      if (current.range?.start) {
        params.desde = current.range.start.toISOString().slice(0, 10);
      }
      if (current.range?.end) {
        params.hasta = current.range.end.toISOString().slice(0, 10);
      }
      if (current.zona) {
        params.zona = current.zona;
      }
      if (current.inmueble) {
        params.inmueble = current.inmueble.trim();
      }
      return params;
    },
    [filters],
  );

  const fetchReport = useCallback(
    async (overrideFilters) => {
      setIsLoading(true);
      try {
        const params = buildParams(overrideFilters);
        const response = await axiosInstance.get(REPORTES_URL, { params });
        setData(response.data);
      } catch (error) {
        notifyError(
          "No pudimos obtener el reporte de zonas sociales. Intenta nuevamente.",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [buildParams],
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const resumenGlobal = useMemo(
    () => data.resumen_global || {},
    [data.resumen_global],
  );
  const porZona = useMemo(() => data.por_zona || [], [data.por_zona]);
  const porInmueble = useMemo(
    () => data.por_inmueble || [],
    [data.por_inmueble],
  );

  const handleClearFilters = useCallback(() => {
    setFilters(defaultFilters);
    fetchReport(defaultFilters);
  }, [fetchReport]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center mb-4">
        <div className="me-auto">
          <h2 className="mb-0">Reportes de zonas sociales</h2>
          <div className="text-muted small">
            Analiza uso, ocupación y horas reservadas por zona y por inmueble.
          </div>
        </div>
        <div className="d-flex gap-2 ms-auto">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleClearFilters}
            disabled={isLoading}
          >
            Limpiar filtros
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => fetchReport()}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="d-inline-flex align-items-center gap-2">
                <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />
                <span>Consultando...</span>
              </span>
            ) : (
              <>
                <i className="fas fa-search me-2" />
                Consultar
              </>
            )}
          </button>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-lg-4 col-md-6">
              <label className="form-label">Rango de fechas</label>
              <DatePickerRanger
                startDate={filters.range.start}
                endDate={filters.range.end}
                onChange={handleRangeChange}
                selectsRange
                customInput={
                  <input
                    className="form-control"
                    placeholder="Selecciona un rango"
                    readOnly
                  />
                }
              />
            </div>

            <div className="col-lg-4 col-md-6">
              <ZonaSocialSelect
                value={filters.zona}
                onChange={handleZonaChange}
                options={zones}
                autoFetch={false}
                allowAllOption
                allOptionValue=""
                allOptionLabel="Todas las zonas"
                allowEmptyOption
                showLabel
                label="Zona social"
                loading={isLoadingZones}
                containerClassName="w-100"
                className="form-control"
              />
            </div>

            <div className="col-lg-4 col-md-6">
              <label className="form-label" htmlFor="reporte-inmueble">
                Inmueble / unidad
              </label>
              <input
                id="reporte-inmueble"
                type="text"
                className="form-control"
                value={filters.inmueble}
                onChange={handleInmuebleChange}
                placeholder="Interior"
              />
            </div>
          </div>
          <div className="row mt-1">
            <div className="col-12">
              <div className="form-text mb-0">
                Si lo dejas vacío, se usa el mes actual por defecto.
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card">
          <div className="card-body">
            <div className="py-5 text-center">
              <SpinnerCitoPlus text="Cargando reporte de zonas sociales..." />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Resumen global</h5>
              <small className="text-muted">
                Periodo: {data.periodo?.desde || "—"} al{" "}
                {data.periodo?.hasta || "—"}
              </small>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-4 mb-3">
                  <div className="text-muted">Reservas totales</div>
                  <div className="fs-4 fw-bold">
                    {formatNumber(resumenGlobal.total_reservas)}
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="text-muted">Horas reservadas</div>
                  <div className="fs-4 fw-bold">
                    {formatHours(resumenGlobal.total_horas_reservadas)}
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="text-muted">Ingresos generados</div>
                  <div className="fs-4 fw-bold">
                    ${formatNumber(resumenGlobal.ingresos_generados)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Detalle por zona</h5>
              <small className="text-muted">
                Incluye ocupación, horas reservadas y estados por zona social.
              </small>
            </div>
            <div className="card-body table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>Zona</th>
                    <th className="text-end">Reservas</th>
                    <th className="text-end">Confirmadas</th>
                    <th className="text-end">Canceladas</th>
                    <th className="text-end">Incumplidas</th>
                    <th className="text-end">Pendientes</th>
                    <th className="text-end">Horas reservadas</th>
                    <th className="text-end">Horas disponibles</th>
                    <th className="text-end">% Ocupación</th>
                    <th className="text-end">Ingresos</th>
                  </tr>
                </thead>
                <tbody>
                  {porZona.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">
                        No hay datos para el rango seleccionado.
                      </td>
                    </tr>
                  ) : (
                    porZona.map((item) => (
                      <tr key={item.zona_uuid}>
                        <td className="text-nowrap">{item.zona_nombre}</td>
                        <td className="text-end">
                          {formatNumber(item.total_reservas)}
                        </td>
                        <td className="text-end">
                          {formatNumber(item.reservas_confirmadas)}
                        </td>
                        <td className="text-end">
                          {formatNumber(item.reservas_canceladas)}
                        </td>
                        <td className="text-end">
                          {formatNumber(item.reservas_incumplidas)}
                        </td>
                        <td className="text-end">
                          {formatNumber(item.reservas_pendientes)}
                        </td>
                        <td className="text-end">
                          {formatHours(item.horas_reservadas)}
                        </td>
                        <td className="text-end">
                          {formatHours(item.horas_disponibles)}
                        </td>
                        <td className="text-end">
                          {Number.isFinite(item.porcentaje_ocupacion)
                            ? `${item.porcentaje_ocupacion.toFixed(1)}%`
                            : "0.0%"}
                        </td>
                        <td className="text-end">
                          ${formatNumber(item.ingresos_generados)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Detalle por inmueble / residente</h5>
              <small className="text-muted">
                Agrupa las reservas confirmadas por unidad o vivienda.
              </small>
            </div>
            <div className="card-body table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>Unidad</th>
                    <th>Residente</th>
                    <th className="text-end">Reservas</th>
                    <th className="text-end">Horas reservadas</th>
                  </tr>
                </thead>
                <tbody>
                  {porInmueble.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        No hay datos para el rango seleccionado.
                      </td>
                    </tr>
                  ) : (
                    porInmueble.map((item, index) => (
                      <tr
                        key={`${item.unidad}-${item.residente_nombre}-${index}`}
                      >
                        <td className="text-nowrap">{item.unidad}</td>
                        <td className="text-nowrap">{item.residente_nombre}</td>
                        <td className="text-end">
                          {formatNumber(item.total_reservas)}
                        </td>
                        <td className="text-end">
                          {formatHours(item.horas_reservadas)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ReportesZonasSociales;
